import { useQuery } from "@tanstack/react-query";

import type { ReviewFilters } from "@/lib/api/endpoints";
import {
  getAgent,
  getGithubInstallation,
  getGovernanceSettings,
  getGovernanceSummary,
  getMe,
  getModelCatalog,
  getMyTenant,
  listPlatformEmbeddingModels,
  listPlatformLlmModels,
  getTenant,
  getKnowledgeBase,
  getVersion,
  getVersionLlm,
  listAttached,
  listAttachedKbs,
  listAttachedValidators,
  listCapabilities,
  getPrReview,
  listEmbeddingModels,
  listVectorStores,
  listGovernanceActivity,
  listGovernedRepos,
  listKnowledgeBases,
  listLlmModels,
  listMyAgents,
  listPrReviews,
  listMyDomains,
  listSkills,
  listTenants,
  listMembers,
  listTrustedSystems,
  listValidatorCatalog,
  listVersions,
} from "@/lib/api/endpoints";
import { qk } from "./keys";

export const useMe = () =>
  useQuery({
    queryKey: qk.me,
    queryFn: getMe,
    retry: false,
    // Poll so an idle session that expires server-side is detected within a
    // minute (the 401 then triggers the global auto-logout) rather than only on
    // the next user action.
    refetchInterval: 60_000,
  });

export const useTenants = () => useQuery({ queryKey: qk.tenants, queryFn: listTenants });

export const useTrustedSystems = () =>
  useQuery({ queryKey: qk.trustedSystems, queryFn: listTrustedSystems });

export const useMembers = () => useQuery({ queryKey: qk.members, queryFn: listMembers });

export const useTenant = (slug: string) =>
  useQuery({ queryKey: qk.tenant(slug), queryFn: () => getTenant(slug), enabled: !!slug });

export const useMyTenant = () => useQuery({ queryKey: qk.myTenant, queryFn: getMyTenant });
export const useMyDomains = () => useQuery({ queryKey: qk.myDomains, queryFn: listMyDomains });
export const useMyAgents = () => useQuery({ queryKey: qk.myAgents, queryFn: listMyAgents });

export const useAgent = (slug: string) =>
  useQuery({ queryKey: qk.agent(slug), queryFn: () => getAgent(slug), enabled: !!slug });

export const useVersions = (slug: string) =>
  useQuery({ queryKey: qk.versions(slug), queryFn: () => listVersions(slug), enabled: !!slug });

export const useVersion = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.version(slug, label),
    queryFn: () => getVersion(slug, label),
    enabled: !!slug && !!label,
  });

export const useVersionLlm = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.versionLlm(slug, label),
    queryFn: () => getVersionLlm(slug, label),
    enabled: !!slug && !!label,
  });

export const useSkills = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.skills(slug, label),
    queryFn: () => listSkills(slug, label),
    enabled: !!slug && !!label,
  });

export const useCapabilities = () =>
  useQuery({ queryKey: qk.capabilities, queryFn: listCapabilities });

export const useAttached = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.attached(slug, label),
    queryFn: () => listAttached(slug, label),
    enabled: !!slug && !!label,
  });

export const useValidatorCatalog = () =>
  useQuery({ queryKey: qk.validatorCatalog, queryFn: listValidatorCatalog });

export const useAttachedValidators = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.attachedValidators(slug, label),
    queryFn: () => listAttachedValidators(slug, label),
    enabled: !!slug && !!label,
  });

// Knowledge bases. While a KB is ingesting we poll so the status flips to
// `ready`/`failed` without a manual refresh.
export const useKnowledgeBases = () =>
  useQuery({
    queryKey: qk.knowledgeBases,
    queryFn: listKnowledgeBases,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((kb) => kb.status === "ingesting") ? 2500 : false,
  });

export const useKnowledgeBase = (slug: string) =>
  useQuery({
    queryKey: qk.knowledgeBase(slug),
    queryFn: () => getKnowledgeBase(slug),
    enabled: !!slug,
    refetchInterval: (query) => (query.state.data?.status === "ingesting" ? 2500 : false),
  });

export const useAttachedKbs = (slug: string, label: string) =>
  useQuery({
    queryKey: qk.attachedKbs(slug, label),
    queryFn: () => listAttachedKbs(slug, label),
    enabled: !!slug && !!label,
  });

// ---- Agent-config governance (ADR-016) -------------------------------------

export const useGithubInstallation = () =>
  useQuery({ queryKey: qk.githubInstallation, queryFn: getGithubInstallation });

export const useGovernedRepos = (refresh = true) =>
  useQuery({ queryKey: qk.governedRepos, queryFn: () => listGovernedRepos(refresh) });

export const usePrReviews = (filters: ReviewFilters = {}) =>
  useQuery({
    queryKey: qk.prReviews(filters as Record<string, unknown>),
    queryFn: () => listPrReviews(filters),
    // A review that is queued or analyzing finishes in the background; poll so
    // the inbox fills in without the user reaching for refresh.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((r) => r.status === "queued" || r.status === "analyzing")
        ? 5_000
        : false,
  });

export const usePrReview = (reviewId: number | null) =>
  useQuery({
    queryKey: qk.prReview(reviewId ?? 0),
    queryFn: () => getPrReview(reviewId as number),
    enabled: reviewId != null,
    refetchInterval: (query) =>
      query.state.data && ["queued", "analyzing"].includes(query.state.data.status) ? 5_000 : false,
  });

export const useGovernanceActivity = (filters: { repoId?: number; actor?: string } = {}) =>
  useQuery({
    queryKey: qk.governanceActivity(filters),
    queryFn: () => listGovernanceActivity(filters),
  });

export const useGovernanceSummary = () =>
  useQuery({
    queryKey: qk.governanceSummary,
    queryFn: getGovernanceSummary,
    // The dashboard tile is nice-to-have; a tenant without governance enabled
    // gets a 404/503 here and should see nothing rather than an error card.
    retry: false,
  });

// ---- Model registry --------------------------------------------------------

export const useLlmModels = () => useQuery({ queryKey: qk.llmModels, queryFn: listLlmModels });

export const useEmbeddingModels = () =>
  useQuery({ queryKey: qk.embeddingModels, queryFn: listEmbeddingModels });

export const useVectorStores = () =>
  useQuery({ queryKey: qk.vectorStores, queryFn: listVectorStores });

export const useModelCatalog = () =>
  useQuery({
    queryKey: qk.modelCatalog,
    queryFn: getModelCatalog,
    // A superadmin can add or discard a model at any time now, so this is held
    // briefly rather than for the session. The API refuses a discarded model
    // regardless; this only keeps the picker from offering one for long.
    staleTime: 60 * 1000,
  });

export const usePlatformLlmModels = () =>
  useQuery({ queryKey: qk.platformModels("llm"), queryFn: listPlatformLlmModels });

export const usePlatformEmbeddingModels = () =>
  useQuery({ queryKey: qk.platformModels("embedding"), queryFn: listPlatformEmbeddingModels });

export const useGovernanceSettings = () =>
  useQuery({ queryKey: qk.governanceSettings, queryFn: getGovernanceSettings });
